import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight, AlertTriangle } from "lucide-react";
import AuthShell from "../../components/AuthShell/AuthShell";
import PasswordField from "../../components/primitives/PasswordField";
import TextButton from "../../components/primitives/TextButton";
import FormError from "../../components/primitives/FormError";
import { ApiError } from "../../services/api";
import { useAuth } from "../../services/AuthContext";
import "./auth.css";

function ResetPasswordPage() {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const { resetPassword } = useAuth();
    const token = params.get("token");

    const [password, setPassword] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [serverErr, setServerErr] = useState(null);
    const [tokenInvalid, setTokenInvalid] = useState(!token);
    const ref = useRef(null);

    useEffect(() => {
        ref.current?.focus?.();
    }, []);

    const submit = async (e) => {
        e?.preventDefault?.();
        setServerErr(null);
        if (!password || password.length < 8) {
            setServerErr("Password must be at least 8 characters.");
            return;
        }
        if (password.length > 128) {
            setServerErr("Password must be at most 128 characters.");
            return;
        }
        setSubmitting(true);
        try {
            await resetPassword(token, password);
            navigate("/dashboard", { replace: true });
        } catch (err) {
            const code = err instanceof ApiError ? err.code : null;
            if (code === "invalid_token") {
                setTokenInvalid(true);
                setServerErr(
                    "This reset link is invalid, expired, or already used."
                );
            } else if (code === "validation_error") {
                setServerErr(err.message || "Password rejected.");
            } else if (code === "too_many_requests") {
                setServerErr("Too many attempts. Try again in a minute.");
            } else {
                setServerErr(
                    err instanceof ApiError
                        ? err.message
                        : err?.message || "Reset failed."
                );
            }
        } finally {
            setSubmitting(false);
        }
    };

    if (tokenInvalid && !submitting) {
        return (
            <AuthShell
                eyebrow="Console / Reset password"
                title="Link invalid or expired."
                lede={
                    serverErr ||
                    "This reset link can't be used. Request a new one from the forgot-password page."
                }
                footer={
                    <>
                        <Link
                            to="/forgot-password"
                            className="auth-shell-link auth-shell-link--accent"
                        >
                            Send a new reset link →
                        </Link>
                    </>
                }
            >
                <div className="auth-check auth-check--error">
                    <div
                        className="auth-check__icon auth-check__icon--error"
                        aria-hidden="true"
                    >
                        <AlertTriangle size={20} />
                    </div>
                </div>
            </AuthShell>
        );
    }

    return (
        <AuthShell
            eyebrow="Console / Reset password"
            title="Set a new password."
            lede="Choose a new password for your account."
            footer={
                <>
                    <Link to="/login" className="auth-shell-link">
                        ← Back to sign-in
                    </Link>
                </>
            }
        >
            <form className="auth-form" onSubmit={submit} noValidate>
                <PasswordField
                    ref={ref}
                    label="New password"
                    required
                    value={password}
                    onChange={setPassword}
                    placeholder="At least 8 characters"
                    autoComplete="new-password"
                    hint="At least 8 characters."
                />

                {serverErr && <FormError>{serverErr}</FormError>}

                <TextButton
                    type="submit"
                    variant="primary"
                    size="lg"
                    fullWidth
                    loading={submitting}
                    iconRight={ArrowRight}
                >
                    Update password & sign in
                </TextButton>
            </form>
        </AuthShell>
    );
}

export default ResetPasswordPage;
